import { useEffect, useState } from 'react';
import { adminApi } from '@/api/endpoints';
import type { PushMessage } from '@/api/types';
import { Alert, Button, Card, Field, Spinner } from '@/components/ui';
import uiStyles from '@/components/ui.module.css';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

const TITLE_MAX = 120;
const BODY_MAX = 500;

/**
 * Titel/Text der automatischen "24h vor Tippschluss"-Erinnerung (Migration 017).
 * Bisher fest in der Server-`.env` (PUSH_TITLE/PUSH_BODY) — jetzt hier frei
 * editierbar, wirkt ab dem nächsten Cron-Lauf ohne Deploy/Server-Zugriff.
 */
export function AdminPushMessagePage() {
  const { data: message, loading, error } = useAsync<PushMessage>(
    (signal) => adminApi.getPushMessage(signal),
    [],
  );

  if (loading) return <Spinner label="Nachricht wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!message) return null;

  return <PushMessageForm initial={message} />;
}

function PushMessageForm({ initial }: { initial: PushMessage }) {
  const [title, setTitle] = useState(initial.title);
  const [body, setBody] = useState(initial.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setSaved(false), [title, body]);

  const titleValid = title.trim() !== '' && title.length <= TITLE_MAX;
  const bodyValid = body.trim() !== '' && body.length <= BODY_MAX;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await adminApi.putPushMessage({ title: title.trim(), body: body.trim() });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Push-Erinnerung</h2>
      <p className="muted">
        Titel und Text der automatischen Erinnerung, die 24h vor Tippschluss an alle
        Abonnenten geschickt wird. Änderungen wirken ab dem nächsten Versand — kein Deploy
        nötig. Hinweis: iOS zeigt unter dem Titel zusätzlich fix „from HoseAus“ an
        (System-Verhalten, nicht beeinflussbar).
      </p>

      <Field
        label={`Titel (${title.length}/${TITLE_MAX})`}
        id="push-title"
        value={title}
        maxLength={TITLE_MAX}
        onChange={(e) => setTitle(e.target.value)}
      />

      <label className={uiStyles.field} htmlFor="push-body">
        <span className={uiStyles.label}>
          Text ({body.length}/{BODY_MAX})
        </span>
        <textarea
          id="push-body"
          className={uiStyles.input}
          rows={3}
          maxLength={BODY_MAX}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>

      {error && <Alert>{error}</Alert>}

      <div className={styles.actions}>
        <Button
          className={styles.btnSm}
          disabled={busy || !titleValid || !bodyValid}
          onClick={() => void save()}
        >
          {busy ? '…' : 'Speichern'}
        </Button>
      </div>

      {saved && <div className={styles.savedHint}>Gespeichert.</div>}
    </Card>
  );
}
